import { environment } from '../../environment'
import { constants } from '../../constants'
import { getLocalizedString } from './helper'
import { getPublicHotelierProperty } from '../../api'

const apiBaseUrl = environment.CONFIG_API_BASE_URL_DRIVE_API_GATEWAY
let token = null

export function getToken () {
  return token
}

export function setToken (newToken) {
  token = newToken
}

const fetchThenHandler = async (response) => {
  if (response.status === 403) {
    throw new Error('NOT_AUTHORIZED')
  }
  if (!response.ok) {
    throw new Error(`Request failed with status: ${response.status}, ${response.json}`)
  }
  return response.json()
}

const createHeaders = (additionalHeaders = {}) => {
  return new Headers({
    'Authorization': `Bearer ${getToken()}`,
    'Content-Type': 'application/json',
    ...additionalHeaders,
  })
}

export function authenticate (authToken = false, abortController = new AbortController()) {
  const headers = createHeaders({
    'Accept': 'application/json',
    'X-Auth-Provider': 'MyGiataDrive',
    'X-Auth-Token': authToken,
  })
  const fetchPromise = fetch(`${apiBaseUrl}/authenticate`, {
    method: 'GET',
    headers,
    signal: abortController.signal,
  }).then(fetchThenHandler)

  return { fetchPromise, abortController }
}

export function getItem (id = null, giataId = null, abortController = new AbortController()) {
  let endpoint = id ? `/${id.replace('/', '')}` : ''
  if (giataId) {
    endpoint = `/giata.id/${giataId}`
  }
  const fetchPromise = fetch(`${apiBaseUrl}${endpoint}`, {
    method: 'GET',
    cache: 'no-cache',
    headers: createHeaders(),
    signal: abortController.signal,
  }).then(fetchThenHandler)
  return { fetchPromise, abortController }
}

function createFetchOptions (abortController, method = 'GET') {
  return {
    method,
    cache: 'no-cache',
    headers: createHeaders(),
    signal: abortController.signal,
  }
}

export function getOptions (giataId, abortController = new AbortController()) {
  const endpoint = `/_/options/${giataId}/`
  const fetchPromise = fetch(
    `${apiBaseUrl}${endpoint}`, createFetchOptions(abortController)
  ).then(fetchThenHandler)
  return { fetchPromise, abortController }
}

export function getScores (giataId, overrideImageCount = null, abortController = new AbortController()) {
  const queryParams = overrideImageCount !== null ? `?overrideImageCount=${overrideImageCount}` : ''
  const endpoint = `/_/scores/properties/${giataId}${queryParams}`
  const fetchPromise = fetch(
    `${apiBaseUrl}${endpoint}`,
    createFetchOptions(abortController)
  ).then(fetchThenHandler)
  return { fetchPromise, abortController }
}

export function getDestinationScoresAvg (destinationId, abortController = new AbortController()) {
  const endpoint = `/_/scores/destinations/${destinationId.replace('/', '')}/avg/`
  const fetchPromise = fetch(
    `${apiBaseUrl}${endpoint}`, createFetchOptions(abortController)
  ).then(fetchThenHandler)
  return { fetchPromise, abortController }
}

export function getDestinationScoresTop (destinationId, scoreId, abortController = new AbortController()) {
  const endpoint = `/_/scores/destinations/${destinationId.replace('/', '')}/top/?indicator=${scoreId}`
  const fetchPromise = fetch(
    `${apiBaseUrl}${endpoint}`, createFetchOptions(abortController)
  ).then(fetchThenHandler)
  return { fetchPromise, abortController }
}

export function getTourOperators (giataId, abortController = new AbortController()) {
  const endpoint = `/_/legacy/properties/${giataId}/tourOperators`
  const fetchPromise = fetch(
    `${apiBaseUrl}${endpoint}`, createFetchOptions(abortController)
  ).then(fetchThenHandler)
    .then((data) => {
      let operators = 0
      let offers = 0
      data.tourOperators.forEach((operator) => {
        operators++
        offers += operator.offers.length
        operator.push = operator.offers.some(
          (offer) => offer.hotelierContentAware?.images
        )
      })
      data.operators = operators
      data.offers = offers
      data.giataId = giataId
      return data
    })

  return { fetchPromise, abortController }
}

export function getSuppliers (giataId, abortController = new AbortController()) {
  const endpoint = '/_/legacy/properties/' + giataId + '/suppliers'
  const fetchPromise = fetch(
    `${apiBaseUrl}${endpoint}`, createFetchOptions(abortController)
  ).then(fetchThenHandler)
    .then((data) => {
      let suppliers = 0
      let records = 0
      data.suppliers.forEach((supplier) => {
        supplier.collapsibleHidden = false
        suppliers++
        records += supplier.records.length
      })
      data.suppliersCount = suppliers
      data.recordsCount = records
      data.giataId = giataId
      return data
    })
  return { fetchPromise, abortController }
}

export function getCountries (abortController = new AbortController()) {
  const endpoint = '/_/search/list/'
  const fetchPromise = fetch(
    `${apiBaseUrl}${endpoint}`, {
      ...createFetchOptions(abortController, 'POST'),
      body: JSON.stringify({
        types: ['08eeab7a-82df-42cf-9e48-fb6a093914ab']
      })
    }
  ).then(fetchThenHandler)
    .then((data) => {
      const codes = {}
      if (data.items !== undefined) {
        data.items.forEach((country) => {
          const countryCode = country.attr[constants.attributePaths.country_code]?.[1]?.data?.code
          const countryName = country.attr[constants.attributePaths.name]?.[1]?.data?.string ?? ''
          if (!countryCode) {
            throw new Error('could not find countryCode for country')
          }
          codes[countryCode] = { code: countryCode, name: countryName }
        })
      }
      data.codes = codes
      return data
    })
  return { fetchPromise, abortController }
}

export function getOffer (giataId, operatorId, offerId, abortController = new AbortController()) {
  const endpoint = `/_/legacy/properties/${giataId}/tourOperators/${operatorId}/`
  const fetchOptions = createFetchOptions(abortController)
  const fetchPromise = fetch(`${apiBaseUrl}${endpoint}`, fetchOptions)
    .then(fetchThenHandler)
    .then((data) => {
      const thisOffer = data.offers.find((offer) => offer.offerId === offerId)
      if (thisOffer) {
        thisOffer.logo = data.logo
      }
      return thisOffer
    })
  return { fetchPromise, abortController }
}

export function getHotels (
  query = '',
  distance = null,
  location = null,
  abortController = new AbortController()
) {
  const endpoint = `/_/search/lookup/`
  const body = {
    query
  }
  if (distance && location) {
    body.geopoint = {
      distance: parseInt(distance, 10) * 1000,
      latitude: location.lat,
      longitude: location.lon
    }
  }
  const fetchOptions = createFetchOptions(abortController, 'POST')
  const fetchPromise = fetch(`${apiBaseUrl}${endpoint}`, {
    ...fetchOptions,
    body: JSON.stringify(body)
  }).then(fetchThenHandler)
  return { fetchPromise, abortController }
}

export function getRanking (
  itemIds,
  abortController = new AbortController()
) {
  const fetchPromise = getHotelsWithScores(itemIds, abortController).then((hotels) => {
    return {
      hotels: hotels.map(({ name, id }) => ({
        name,
        hotelId: id,
      })),
      scores: hotels[0].scores.reduce((scores, score, index) => {
        scores[score.id] = {
          items: hotels
            .filter((hotel) => hotel.scores)
            .map(({ name, id, scores: hotelScores }) => {
              return {
                name,
                hotelId: id,
                value: hotelScores[index].percentage * 100,
                percentage: hotelScores[index].percentage,
              }
            })
            .sort((a, b) => a.value - b.value)
            .reverse(),
        }
        return scores
      }, {}),
    }
  })
  return { fetchPromise, abortController }
}

export function getHotelsWithScores (itemIds, abortController = new AbortController()) {
  return Promise.allSettled(
    itemIds.map(async (itemId) => {
      const data = await getItem(itemId, null, abortController).fetchPromise
      if (data.items[0].attr[constants.attributePaths.giataId]?.[1]?.data?.code) {
        const giataId = data.items[0].attr[constants.attributePaths.giataId][1].data.code
        const publicHotelierProperty = await getPublicHotelierProperty(giataId).fetchPromise
        const overrideImageCount = publicHotelierProperty?.images?.countConfirmed ?? null
        const scores = await getScores(
          giataId,
          overrideImageCount,
          abortController
        ).fetchPromise
        return {
          name: getLocalizedString(data.items[0], 'name'),
          id: data.items[0].id,
          giataId: giataId,
          scores: scores.scores,
        }
      }
      return Promise.reject(itemId)
    })
  ).then((results) => {
    return results.filter((result) => result.status === 'fulfilled').map((result) => result.value)
  })
}
